import { NextPage } from "next";
import { useKeycloak } from "@react-keycloak-fork/ssr";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { PageProps } from "@/pages/_app";
import { Layout } from "@/components/layout/Layout";
import { routes } from "@/routes/routes";
import HeroBlock from "@/components/common/heroBlock/HeroBlock";
import { Section } from "@/components/common/section/Section";
import { sendFetchRequest } from "@/utils/sendFetchRequest";
import { UserDetails } from "@/interfaces/userDetails";
import {
  Reservation,
  ReservationsResponse,
  sendFetchUserReservations,
} from "@/components/pages/account/_helpers/useFetchUserReservations";
import { StrapiImage } from "@/utils/strapi";
import { NetworkError } from "@/interfaces/errors/networkError";
import { AccountMenu } from "./accountMenu/AccountMenu";
import { Locations } from "./locations/Locations";
// import { Billing } from "./billing/Billing";
import { MyAccount } from "./myAccount/MyAccount";
import { Drivers } from "./drivers/Drivers";
import { AccountError } from "./accountError/AccountError";
import styles from "./Account.module.scss";
import { UserInvalidate } from "./userInvalidate/UserInvalidate";

type AccountProps = PageProps & {
  categoriesImages: {
    [name: string]: StrapiImage[] | undefined;
  };
};

export enum AccountTabType {
  LOCATIONS = "locations",
  BILLING = "billing",
  MY_ACCOUNT = "myAccount",
  DRIVERS = "drivers",
}

export const Account: NextPage<AccountProps> = ({
  layout,
  categoriesImages,
}: AccountProps) => {
  const intl = useIntl();

  const { keycloak, initialized } = useKeycloak();
  const [isUserError, setIsUserError] = useState<boolean>(false);
  const [isUserInvalidate, setIsUserInvalidate] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserDetails>();
  const [reservationList, setReservationList] = useState<Reservation[]>([]);
  const [lastname, setLastname] = useState<string>("");
  const [firstname, setFirstname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState<AccountTabType>(
    AccountTabType.LOCATIONS
  );

  const getUserData = (token?: string) => {
    return new Promise<UserDetails>((resolve) => {
      sendFetchRequest(`/customers/v1`, token)
        .then((json: UserDetails) => {
          resolve(json);
        })
        .catch((e: NetworkError<null>) => {
          if (e.message === "204") {
            setIsUserInvalidate(true);
          } else {
            setIsUserError(true);
          }
        });
    });
  };

  const getUserReservations = (token?: string) => {
    return new Promise<ReservationsResponse>((resolve) => {
      sendFetchUserReservations(token)
        .then((json: ReservationsResponse) => {
          resolve(json);
        })
        .catch((e) => {
          console.log(e);
        });
    });
  };

  useEffect(() => {
    if (initialized && keycloak) {
      if (
        keycloak.tokenParsed &&
        typeof keycloak.tokenParsed.email === "string"
      ) {
        setLastname(keycloak.tokenParsed.family_name as unknown as string);
        setFirstname(keycloak.tokenParsed.given_name as unknown as string);
        setEmail(keycloak.tokenParsed.email as unknown as string);
        getUserData(keycloak.idToken)
          .then((value: UserDetails) => {
            setUserData(value);
          })
          .catch((e) => {
            console.log(e);
          });
        getUserReservations(keycloak.token)
          .then((response) => {
            setReservationList(response.data);
          })
          .catch((e) => {
            // setIsUserError(true);
            console.log(e);
          });
      }
    }
  }, [initialized, keycloak]);

  return (
    <>
      {((!isUserError && userData && !isUserInvalidate) ||
        isUserError ||
        isUserInvalidate) && (
        <Layout
          layout={layout}
          defaultCanonicalRoute={routes.account}
          title={intl.formatMessage({ id: "myAccount" })}
          breadcrumbs={[
            { title: intl.formatMessage({ id: "home" }), url: "/" },
            {
              title: intl.formatMessage({ id: `myAccount` }),
              url: "/account/dashboard",
            },
          ]}
        >
          <HeroBlock
            title={intl.formatMessage({ id: "myAccount" })}
            hideSearchBlock
          />
          {!isUserError && userData && (
            <>
              <AccountMenu
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                lastname={userData.lastname}
                firstname={userData.firstname}
              />
              <Section containerStyle={styles.section}>
                {selectedTab === AccountTabType.LOCATIONS && (
                  <Locations
                    reservationList={reservationList}
                    categoriesImages={categoriesImages}
                  />
                )}
                {/* {selectedTab === AccountTabType.BILLING && <Billing />} */}
                {selectedTab === AccountTabType.MY_ACCOUNT && (
                  <MyAccount
                    userData={userData}
                    userToken={keycloak?.idToken}
                    email={email}
                  />
                )}
                {selectedTab === AccountTabType.DRIVERS && <Drivers />}
              </Section>
            </>
          )}
          {isUserError && (
            <AccountError lastname={lastname} firstname={firstname} />
          )}
          {isUserInvalidate && <UserInvalidate email={email} />}
        </Layout>
      )}
    </>
  );
};
